/* eslint-disable complexity */

import type { Currency, Restaurant } from '@local/api';

import type { LocalOrderType } from 'context/OrderContext';
import type { UserDataType } from 'types/customer';
import type { SubmittedOrderType } from 'types/order';

const DELIVERY_FULFILLMENT = 'delivery';

type CartItemInput = {
  modifierNames?: readonly string[];
  size?: string;
  name?: string;
  price?: number;
  priceWithModifiers?: number;
  quantity?: number;
  specialInstructions?: string | null;
};

type CartItemOutput = {
  modifierNames?: readonly string[];
  size: string;
  name?: string;
  price?: number;
  quantity?: number;
  perItemTotal: number;
  specialInstructions?: string | null;
};

/** `pendingOrder` XOR `submittedOrder`, never both */
type OrderParams =
  | { pendingOrder?: LocalOrderType; submittedOrder?: never }
  | { pendingOrder?: never; submittedOrder?: SubmittedOrderType };

type GetRoktAttributesParams = OrderParams & {
  user?: UserDataType;
  /**
   * `Checkout` passes its hook-formatted items (richer than order.items); every
   * other caller can omit them and fall back to the order's own items.
   */
  items?: readonly CartItemInput[];
  restaurant?: Restaurant;
  currency: Currency;
};

/**
 * Extracts and reshapes cart items into the flat, priced form Rokt expects.
 */
export const getCartItems = (
  items: readonly CartItemInput[] = []
): CartItemOutput[] =>
  items.map((item) => {
    const price = item.priceWithModifiers || item.price;
    return {
      modifierNames: item.modifierNames,
      size: item.size || 'Regular',
      name: item.name,
      price,
      quantity: item.quantity,
      perItemTotal: (price ?? 0) / (item.quantity ?? 1),
      specialInstructions: item.specialInstructions,
    };
  });

/**
 * Builds the attribute object for `mParticle.Rokt.selectPlacements` from this app's
 * domain objects. Every input is optional, returns a best-effort set.
 */
export const getRoktAttributes = ({
  user,
  pendingOrder,
  submittedOrder,
  items,
  restaurant,
  currency,
}: GetRoktAttributesParams): RoktAttributes => {
  const currentOrder = submittedOrder ?? pendingOrder;
  const customer = submittedOrder?.customer;
  const person = customer ?? user;
  const mobile = customer?.phone_number || user?.phone?.number;
  const billingzipcode = customer?.billing?.address?.zip;
  const paymenttype = customer?.billing?.card?.type;

  return {
    country: restaurant?.address?.country_alpha2 ?? '',
    currency,
    language: 'en',
    cartitems: JSON.stringify(getCartItems(items ?? currentOrder?.items)),
    ...(person?.email && { email: person.email }),
    ...(person?.first_name && { firstname: person.first_name }),
    ...(person?.last_name && { lastname: person.last_name }),
    ...(mobile && { mobile }),
    ...(billingzipcode && { billingzipcode }),
    ...(paymenttype && { paymenttype }),
    ...(person && { customertype: person.first_name ? 'member' : 'guest' }),
    ...(currentOrder?.total !== undefined && { amount: currentOrder.total }),
    ...(submittedOrder?.id !== undefined && {
      confirmationref: submittedOrder.id,
    }),
    ...(currentOrder?.fulfill_method !== undefined && {
      delivery: currentOrder.fulfill_method === DELIVERY_FULFILLMENT,
    }),
    ...(restaurant?.cuisines && { majorcat: restaurant.cuisines.join(', ') }),
    ...(restaurant?.id && { restaurantId: restaurant.id }),
    ...(restaurant?.address?.city && {
      restaurantCity: restaurant.address.city,
    }),
  };
};
